import {
  E_QUERY_ITEM_CONDITION,
  IDrugDevStatusSearchField,
  IQuery,
  IQueryItem,
  IQueryItemField,
  IQueryValueRange,
} from '@patsnap/synapse_common_interface'
import { fieldGroupsBasis, IFieldGroup, IFieldGroupMeta } from '@pharmsnap/pharmsnap-web/composable/useDrugFinderExpertSearch'
import { CustomDateRageConfig, DrugFinderExpertKey, DRUG_MILESTONE_PHASE_FIELDS } from '@pharmsnap/shared/src/utils'
import { getGroupField } from '../components/DrugFinderExpertSearch/config/fieldConfig'

/**
 * 将 Advanced Query 转换为 Expert Query
 * @param advancedQuery - ADVANCED 类型的 query items
 * @returns 转换后的 Expert Query
 */
export function convertAdvancedToExpertQuery(advancedQuery: IQueryItem[]): IQueryItem {
  // 步骤1: Fields 映射
  const mappedQuery = mapAdvancedFieldsToExpert(advancedQuery)

  // 步骤2: advancedQuery 转换为 expertQuery
  const expertQuery = processAdvancedQueryToExpertQuery(mappedQuery)

  // 步骤3: 组合检索项分组
  const groupedQuery = groupCombinedFields(expertQuery)

  // 步骤4: 补充 Meta 信息
  const queryWithMeta = addMetaInformation(groupedQuery)
  return queryWithMeta
}

/**
 * 建立 field 到 fieldGroup 的映射
 * @returns Map<string, IFieldGroup & { fieldKey: string; originalIndex: number }>
 */
function buildFieldToGroupMap(): Map<string, IFieldGroup & { fieldKey: DrugFinderExpertKey; originalIndex: number }> {
  const fieldToGroupMap = new Map<string, IFieldGroup & { fieldKey: DrugFinderExpertKey; originalIndex: number }>()

  fieldGroupsBasis.forEach((group) => {
    ;(Object.keys(group.fields) as DrugFinderExpertKey[]).forEach((fieldKey) => {
      const fields = getGroupField(group, fieldKey).fields
      fields.forEach((field) => {
        fieldToGroupMap.set(field, {
          ...group,
          fieldKey,
          originalIndex: Number(group.id),
        })
      })
    })
  })
  return fieldToGroupMap
}
/**
 * 步骤 1: Advanced Fields 映射到 Expert Fields
 */
function mapAdvancedFieldsToExpert(queryItems: IQueryItem[]): IQueryItem[] {
  return queryItems.map((item) => {
    if (item.type === 'field') {
      const fields = item.fields

      // 处理初创公司字段
      if (fields.length === 1 && fields[0] === 'IS_STARTUPS') {
        return {
          ...item,
          fields: ['DEV_STATUS_ORG_IS_STARTUPS'],
        }
      }

      // 处理机构字段（需要判断组合）
      if (isOrganizationFields(fields)) {
        return {
          ...item,
          fields: mapOrganizationFields(fields),
        }
      }
    }

    return item
  })
}

/**
 * 判断是否为机构字段
 */
function isOrganizationFields(fields: string[]): boolean {
  const orgFields = ['ORGANIZATION_ID_ONEID', 'ORIGINAL_DEV_ORG_ID_ONEID', 'LICENSE_ORG_ID_ONEID']
  return fields.some((f) => orgFields.includes(f))
}

/**
 * 映射机构字段
 */
function mapOrganizationFields(fields: string[]): string[] {
  const fieldSet = new Set(fields)
  const result = new Set<string>()

  // 在研/非在研机构
  if (fieldSet.has('ORGANIZATION_ID_ONEID') && fieldSet.size === 1) {
    result.add('DEV_ORG_ID_ONEID')
    result.add('NON_DEV_ORG_ID_ONEID')
  }
  // 原研/在研/非在研机构
  else if (fieldSet.has('ORIGINAL_DEV_ORG_ID_ONEID') && fieldSet.has('ORGANIZATION_ID_ONEID') && fieldSet.size === 2) {
    result.add('DEV_ORG_ID_ONEID')
    result.add('NON_DEV_ORG_ID_ONEID')
    result.add('ORIGINAL_DEV_ORG_ID_ONEID')
  }
  // 权益/原研/在研/非在研机构
  else if (fieldSet.has('LICENSE_ORG_ID_ONEID') && fieldSet.has('ORIGINAL_DEV_ORG_ID_ONEID') && fieldSet.has('ORGANIZATION_ID_ONEID')) {
    result.add('DEV_ORG_ID_ONEID')
    result.add('NON_DEV_ORG_ID_ONEID')
    result.add('ORIGINAL_DEV_ORG_ID_ONEID')
    result.add('LICENSE_ORG_ID_ONEID')
  }
  // 其他情况保持不变
  else {
    return fields
  }

  return Array.from(result)
}

/**
 * 需要在 condition=all 时拆分为多个独立查询项的字段列表
 * 这些字段在 ADVANCED 模式下支持 condition=all（全部包含），
 * 转换为 EXPERT 模式时需要拆分为多个独立的 AND 条件
 */
const FIELDS_NEED_SPLIT_WHEN_ALL = ['TARGET_ID', 'ALL_FIELDS']

/**
 * 判断字段是否需要在 condition=all 时拆分
 */
function shouldSplitWhenConditionAll(field: string): boolean {
  return FIELDS_NEED_SPLIT_WHEN_ALL.includes(field)
}

/**
 * 通用拆分函数 - 将 condition=all 且有多个值的查询项拆分为多个独立的查询项
 * @param queryItem - 需要拆分的 query item
 * @returns 拆分后的 query items 数组
 *
 * @example
 * 输入: { type: 'field', fields: ['TARGET_ID'], condition: 'all', value: [val1, val2, val3] }
 * 输出: [
 *   { type: 'field', fields: ['TARGET_ID'], condition: 'any', value: [val1] },
 *   { type: 'field', fields: ['TARGET_ID'], condition: 'any', value: [val2] },
 *   { type: 'field', fields: ['TARGET_ID'], condition: 'any', value: [val3] }
 * ]
 */
function splitQueryItemByValue(queryItem: IQueryItemField): IQueryItemField[] {
  return queryItem.value.map((singleValue) => ({
    type: 'field',
    fields: queryItem.fields,
    condition: E_QUERY_ITEM_CONDITION.ANY,
    value: [singleValue],
  })) as IQueryItemField[]
}

/**
 * 步骤 2: 将 Advanced Query Item 转换为 Expert Query Item
 */
function processAdvancedQueryToExpertQuery(queryItems: IQueryItem[]): IQueryItem[] {
  const result: IQueryItem[] = []

  for (const item of queryItems) {
    if (item.type === 'field') {
      const field = item.fields[0]
      const needSplit = shouldSplitWhenConditionAll(field) && item.condition === 'all' && item.value.length > 1

      if (needSplit) {
        // 拆分为多个独立的查询项
        const splitQueries = splitQueryItemByValue(item)
        result.push(...splitQueries)
      } else {
        result.push(item)
      }
    } else {
      result.push(item)
    }
  }

  return result
}
/**
 * 步骤 3: 组合检索项分组
 */
function groupCombinedFields(queryItems: IQueryItem[]): IQueryItem[] {
  // 建立 field 到 fieldGroup 的映射
  const fieldToGroupMap = buildFieldToGroupMap()

  // 按组分类
  const groupMap = new Map<string, IQueryItem[]>()
  const ungroupedItems: IQueryItem[] = []

  queryItems.forEach((item) => {
    if (item.type === 'field') {
      const field = item.fields[0]
      const groupInfo = fieldToGroupMap.get(field)

      if (groupInfo && groupInfo.type === 'combined') {
        const groupId = groupInfo.id
        if (!groupMap.has(groupId)) {
          groupMap.set(groupId, [])
        }
        groupMap.get(groupId)?.push(item)
      } else {
        ungroupedItems.push(item)
      }
    } else {
      ungroupedItems.push(item)
    }
  })

  // 组装结果
  const result: IQueryItem[] = []

  // 添加组合检索项
  groupMap.forEach((items) => {
    if (items.length > 1) {
      // 多个字段，包装成 group
      result.push({
        type: 'group',
        must: items,
      })
    } else {
      // 单个字段，直接添加
      result.push(items[0])
    }
  })

  // 添加未分组的项
  result.push(...ungroupedItems)

  return result
}

// ========================================
// 辅助函数：Meta 信息处理
// ========================================

/**
 * 创建统一的 Meta 信息对象
 */
export function createMetaInfo(
  groupInfo: IFieldGroup,
  fieldKey: DrugFinderExpertKey,
  index: number,
  logic: 'AND' | 'OR' | 'NOT' = 'AND'
): IFieldGroupMeta {
  const originalId = String(index) || groupInfo.id
  return {
    fieldKey,
    originalProperty: groupInfo.property || '',
    originalType: groupInfo.type,
    originalId,
    originalIndex: Number(originalId),
    originalLogic: logic,
  }
}

/**
 * 获取查询项的原始索引（用于排序）
 */
function getQueryItemOriginalIndex(
  item: IQueryItem,
  fieldToGroupMap: Map<string, IFieldGroup & { fieldKey: DrugFinderExpertKey; originalIndex: number }>
): number {
  if (item.type === 'field') {
    const field = item.fields[0]
    const groupInfo = fieldToGroupMap.get(field)
    return groupInfo?.originalIndex ?? Number.MAX_SAFE_INTEGER
  }

  if (item.type === 'group' && item.must && item.must.length > 0) {
    // 对于 group，使用第一个 field 的 originalIndex
    const firstField = item.must.find((subItem) => subItem.type === 'field')
    if (firstField && firstField.type === 'field') {
      const field = firstField.fields[0]
      const groupInfo = fieldToGroupMap.get(field)
      return groupInfo?.originalIndex ?? Number.MAX_SAFE_INTEGER
    }
  }

  return Number.MAX_SAFE_INTEGER
}

/**
 * 处理 Milestone 字段的值转换
 */
function processMilestoneFieldValue(item: IQueryItemField): IQueryItemField {
  return {
    ...item,
    value: (item.value as IQueryValueRange[]).map((v) => {
      const valConfig = CustomDateRageConfig.dateRangeItems.find((o) => o.name_en === v.display_name_en)
      const value = valConfig ? `${valConfig.val}-${valConfig.unit}` : v.display_name_en
      return {
        ...v,
        id: `${v.from}-${v.to}`,
        value,
      }
    }),
  }
}

/**
 * 步骤 4: 补充 Meta 信息
 */
function addMetaInformation(queryItems: IQueryItem[]): IQueryItem {
  // 建立 field 到 fieldGroup 的映射
  const fieldToGroupMap = buildFieldToGroupMap()

  // 按 originalIndex 排序
  const sortedItems = [...queryItems].sort((a, b) => getQueryItemOriginalIndex(a, fieldToGroupMap) - getQueryItemOriginalIndex(b, fieldToGroupMap))

  // 全局序号计数器，确保每个查询项的 index 唯一且递增
  let globalIndexCounter = 0

  /**
   * 处理单个 field 类型的查询项
   */
  const processFieldItem = (item: IQueryItemField) => {
    const field = item.fields[0]
    const groupInfo = fieldToGroupMap.get(field)

    if (!groupInfo) {
      return item
    }

    // 使用全局计数器，确保唯一性
    const currentIndex = globalIndexCounter++

    return {
      ...item,
      _meta: createMetaInfo(groupInfo, groupInfo.fieldKey, currentIndex),
    }
  }

  /**
   * 递归处理 group 类型的查询项
   */
  const processGroupItem = (item: IQuery): IQueryItem => {
    if (item.type !== 'group') {
      return item
    }

    // 处理 must 数组
    const processedMust =
      item.must?.map((subItem) => {
        if (subItem.type === 'field') {
          return processFieldItem(subItem)
        } else if (subItem.type === 'group') {
          return processGroupItem(subItem)
        }
        return subItem
      }) || []

    // 处理 any 数组（主要针对 milestone）
    const processedAny =
      item.any?.map((subItem) => {
        if (subItem.type !== 'field') {
          return subItem
        }

        // 检查是否为 milestone 字段
        const isMilestone = subItem.fields.every((f) => DRUG_MILESTONE_PHASE_FIELDS.includes(f as IDrugDevStatusSearchField))

        if (isMilestone) {
          const groupInfo = fieldToGroupMap.get('MILESTONE_DATE')
          if (groupInfo) {
            const currentIndex = globalIndexCounter++
            const itemWithProcessedValue = processMilestoneFieldValue(subItem)
            return {
              ...itemWithProcessedValue,
              _meta: createMetaInfo(groupInfo, groupInfo.fieldKey, currentIndex),
            }
          }
        }

        return subItem
      }) || []

    return {
      ...item,
      must: [...processedMust, ...processedAny] as IQueryItem[],
    }
  }

  // 为每个查询项添加 meta
  const processedItems = sortedItems.map((item) => {
    if (item.type === 'field') {
      return processFieldItem(item)
    }

    if (item.type === 'group') {
      return processGroupItem(item)
    }

    return item
  })

  // 包装为 group 结构
  return {
    type: 'group',
    must: processedItems,
  }
}
